import PostType, { CampaignType } from 'constants/postType.constants';
import { isArray, isString, kebabCase, size } from 'lodash-es';
import getQualifiedImageUrl from './imageUrlProcessor';
import { NEKI_LOGO_S3_URL } from 'constants/neki.constant';
import { thumbnailFetcher } from './thumbnailFetcher';

const getPostTitle = (post) => {
  switch (post.sourcePostType) {
    case PostType.Campaign:
      return post.sourcePostData.title;
    case PostType.Volunteering:
      return post.sourcePostData.volunteerOpportunity.name;
    case PostType.CrowdFunding:
      return post.sourcePostData.title;
    default:
      return null;
  }
};

const generateLinkFromText = (text) => {
  const name = kebabCase(text.substring(0, 32).replace(/[^a-zA-Z0-9\s]/g, ''));
  return name;
};

const getProjectPostLink = (postId, projectName, companySlug) => {
  const name = kebabCase(projectName.substring(0, 32));
  const link = `/${companySlug}/project/${name}-${postId}`;
  return link;
};

const getTwitterPostLink = (postId, companySlug) => {
  const link = `/${companySlug}/feed/post-${postId}`;
  return link;
};

const getVolunteeringPostLink = (postId, opportunityName, companySlug) => {
  const name = generateLinkFromText(opportunityName);
  const link = `/${companySlug}/volunteering-opportunity/${name}-${postId}`;
  return link;
};

const getFundraiserLink = (postId, campaignName, companySlug) => {
  const name = generateLinkFromText(campaignName);
  const link = `/${companySlug}/fundraiser/${name}-${postId}`;
  return link;
};

const getInKindFundraiserLink = (postId, campaignName, companySlug) => {
  const name = generateLinkFromText(campaignName);
  const link = `/${companySlug}/in-kind-fundraiser/${name}-${postId}`;
  return link;
};

const getUserPostLink = (postId, message, companySlug) => {
  const name = message ? generateLinkFromText(message) : 'post';
  const link = `/${companySlug}/feed/${name}-${postId}`;
  return link;
};

const getCelebrationPostLink = (postId, message, companySlug) => {
  const name = message ? generateLinkFromText(message) : 'celebration';
  const link = `/${companySlug}/celebration/${name}-${postId}`;
  return link;
};

const getCrowdFundingLink = (postId, title, companySlug) => {
  const name = generateLinkFromText(title);
  const link = `/${companySlug}/crowd-funding/${name}-${postId}`;
  return link;
};

const getPostLink = (post, orgSlug = null) => {
  let companySlug = orgSlug || post?.renderProps?.company.slug;

  switch (post.sourcePostType) {
    case PostType.UserPost:
      return getUserPostLink(post.id, post.sourcePostData.message, companySlug);
    case PostType.Volunteering:
      const opportunity =
        post.sourcePostData.volunteerOpportunity ||
        post.sourcePostData.volunteeringOpportunity;
      return getVolunteeringPostLink(post.id, opportunity.name, companySlug);
    case PostType.Campaign:
      if (post.sourcePostData.campaignType === CampaignType.FundRaise) {
        return getFundraiserLink(
          post.id || post._id,
          post.sourcePostData.title,
          companySlug,
        );
      }
      return getInKindFundraiserLink(
        post.id || post._id,
        post.sourcePostData.title,
        companySlug,
      );
    case PostType.Celebration:
      return getCelebrationPostLink(
        post.id,
        post.sourcePostData.message,
        companySlug,
      );
    case PostType.NewsFeeds:
      return getTwitterPostLink(post.id, companySlug);
    case PostType.Project:
      return getProjectPostLink(post.id, post.sourcePostData.name, companySlug);
    case PostType.CrowdFunding:
      return getCrowdFundingLink(
        post.id,
        post.sourcePostData.title,
        companySlug,
      );
    default:
      return '';
  }
};

const createPostBreadcrumbs = (post, orgSlug = null) => {
  const steps = [
    {
      label: 'Home',
      href: '/home',
    },
  ];
  if (post) {
    const orgInfo = post.renderProps.company;
    let slug = orgInfo.slug ? `/${orgInfo.slug}` : null;
    if (orgSlug) {
      slug = orgSlug;
    }
    steps.push({
      label: orgInfo.name,
      href: slug,
    });

    const title = getPostTitle(post);

    steps.push({
      label: title || 'Post',
      href: '',
    });
  }
  return steps;
};

const createThumbnail = async (videoUrl) => {
  if (!videoUrl) {
    return null;
  }
  const isPublicUrl =
    !videoUrl.includes('cloudinary') &&
    !videoUrl.includes(process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME);
  if (isPublicUrl) {
    return await thumbnailFetcher(videoUrl);
  }
  let baseVideoUrl = videoUrl.substring(0, videoUrl.lastIndexOf('.'));
  let newExtension = 'jpg';
  let newVideoUrl = baseVideoUrl + '.' + newExtension;
  return newVideoUrl;
};

const getPostImage = (post) => {
  if (
    post.renderProps.imageUrls &&
    isArray(post.renderProps.imageUrls) &&
    size(post.renderProps.imageUrls) > 0 &&
    !!post.renderProps.imageUrls[0]
  ) {
    return {
      url: getQualifiedImageUrl(post.renderProps.imageUrls[0]),
      type: 'post',
    };
  } else if (
    isString(post.renderProps.imageUrls) &&
    size(post.renderProps.imageUrls) > 0
  ) {
    return {
      url: getQualifiedImageUrl(post.renderProps.imageUrls),
      type: 'post',
    };
  } else if (post.renderProps.company.logoUrl) {
    return {
      url: getQualifiedImageUrl(post.renderProps.company.logoUrl),
      type: 'logo',
    };
  }
  return { url: '/assets/images/neki-logo.svg', type: 'logo' };
};

const getPostVideo = (post) => {
  if (
    post.renderProps.videoUrls &&
    isArray(post.renderProps.videoUrls) &&
    size(post.renderProps.videoUrls) > 0 &&
    !!post.renderProps.videoUrls[0]
  ) {
    return { url: post.renderProps.videoUrls[0], type: 'post' };
  } else if (
    isString(post.renderProps.videoUrls) &&
    size(post.renderProps.videoUrls) > 0
  ) {
    return { url: post.renderProps.videoUrls, type: 'post' };
  }
  if (
    post.sourcePostData &&
    post.sourcePostData.volunteeringOpportunity &&
    post.sourcePostData.volunteeringOpportunity.videoUrls &&
    post.sourcePostData.volunteeringOpportunity.videoUrls.length > 0
  ) {
    return {
      url: post.sourcePostData.volunteeringOpportunity.videoUrls[0],
      type: 'post',
    };
  }

  if (
    post.sourcePostData &&
    post.sourcePostData.volunteerOpportunity &&
    post.sourcePostData.volunteerOpportunity.videoUrls &&
    post.sourcePostData.volunteerOpportunity.videoUrls.length > 0
  ) {
    return {
      url: post.sourcePostData.volunteerOpportunity.videoUrls[0],
      type: 'post',
    };
  }
  return {};
};

const getOpenGraphImage = (post) => {
  if (
    isArray(post.renderProps.imageUrls) &&
    size(post.renderProps.imageUrls) > 0 &&
    !!post.renderProps.imageUrls[0]
  ) {
    return getQualifiedImageUrl(post.renderProps.imageUrls[0]);
  } else if (
    isString(post.renderProps.imageUrls) &&
    size(post.renderProps.imageUrls) > 0
  ) {
    return getQualifiedImageUrl(post.renderProps.imageUrls);
  } else if (post.renderProps.nonProfit?.logo_url) {
    return getQualifiedImageUrl(post.renderProps.nonProfit?.logo_url);
  } else if (post.renderProps.company.logoUrl) {
    return getQualifiedImageUrl(post.renderProps.company.logoUrl);
  }
  return NEKI_LOGO_S3_URL;
};

const getOpenGraphVideo = async (post) => {
  const video = getPostVideo(post);
  return video ? await createThumbnail(video?.url) : null;
};

const getPostIndicatorIcon = (post) => {
  switch (post.sourcePostType) {
    case PostType.Campaign:
      if (post.sourcePostData.campaignType === CampaignType.FundRaise) {
        return '/assets/icons/donations-made.svg';
      }
      return '/assets/icons/fundraise-campaign.svg';
    case PostType.Volunteering:
      return '/assets/icons/volunteer.svg';
    case PostType.Project:
      return '/assets/icons/bulb.svg';
    case PostType.CrowdFunding:
      return '/assets/icons/support.svg';
    default:
      return null;
  }
};

const shouldDisablePostCauses = (home, orgSlug) => {
  if (home || orgSlug?.includes('nonprofit')) return true;
  return false;
};

export {
  getPostLink,
  getProjectPostLink,
  getUserPostLink,
  getVolunteeringPostLink,
  getFundraiserLink,
  getInKindFundraiserLink,
  getCelebrationPostLink,
  getTwitterPostLink,
  getCrowdFundingLink,
  getPostTitle,
  createPostBreadcrumbs,
  getPostImage,
  getPostVideo,
  getOpenGraphImage,
  getOpenGraphVideo,
  getPostIndicatorIcon,
  shouldDisablePostCauses,
};
